import { Notify, Screen } from "quasar";
import { computed, inject, type Ref, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import type { PopperProps } from "shared/components/base/BasePopper";
import useStreamFiltering from "shared/composables/useStreamFiltering";
import { isAbortError } from "shared/services/api/isAbortError";
import { fetchMatchingSyndications } from "shared/services/fetch/fetchMatchingSyndications";
import { type Nullable, type Stream } from "shared/types";
import { type MentionWithSyndication } from "shared/types/mentions";

export function useSyndicationsPopper<
  M extends MentionWithSyndication,
  S = M["syndications"][number],
>({
  abortController,
  mention,
  popperAttrs: attrs = {},
  stream,
}: {
  abortController?: Ref<Nullable<AbortController>>;
  mention: M;
  popperAttrs?: Partial<PopperProps>;
  stream: Stream;
}) {
  const isMobile = inject("isMobile");
  const isLoading = ref(false);
  const { filtering, filters } = useStreamFiltering({ stream });
  const syndicationsMatchingStream = ref<Record<string, boolean> | null>(null);

  const popperAttrs = computed<PopperProps>(() => ({
    "click-outside-capture": true,
    disableClickOutside: false,
    fallbackPlacements: ["left"],
    flip: true,
    offset: [0, isMobile ? 0 : 32],
    placement: isMobile ? "bottom" : "right",
    tether: true,
    width: isMobile ? Math.min(Screen.width, 375) : 350,
    ...attrs,
  }));

  const loadSyndicationMatches = async (): Promise<void> => {
    try {
      if (!isLoading.value && stream?.id && !syndicationsMatchingStream.value) {
        isLoading.value = true;

        syndicationsMatchingStream.value = await fetchMatchingSyndications({
          filters: filtering.value ? filters.value.requestFilters() : {},
          mentionId: mention.id,
          mentionType: mention.type,
          options: { signal: abortController?.value?.signal },
          streamId: stream.id,
        });
      }
    } catch (error) {
      if (!isAbortError(error)) {
        Notify.create(
          getLocaleText("syndications.unable_to_load_syndications")
        );
      }
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    loadSyndicationMatches,
    popperAttrs,
    syndications: (mention.syndications ?? []) as S[],
    syndicationsMatchingStream,
  };
}
